/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — the 1–4 severity family (operator-cop-doctrine §3A.7,
 *  §8.4 decision a, 2026-06-03).
 *
 *  This module is the SINGLE source of the 1–4 severity palette. The same four
 *  hexes carry TWO distinct operator axes (the #613 Threat/Response split,
 *  founder §4.3 sign-off 2026-07-10) — never conflate them on an operator surface:
 *
 *    • THREAT LEVEL (THREAT_LEVEL_META) — the MACHINE-DERIVED hazard severity on a
 *      RAW, UN-OWNED hazard (fused hotspot / flood / ice / CAP / OC-fire ladder).
 *      Names: Low / Moderate / High / Very High. This is the axis on every raw-
 *      hazard glance surface AND the SITREP `response_level_max` roll-up (a
 *      machine-dominant max, NOT an IC assessment — founder ruling 2026-07-10).
 *
 *    • RESPONSE LEVEL (RESPONSE_LEVEL_META) — the IC-ASSESSED Ontario IMS §5 Table 1
 *      response STRUCTURE (Small / Large / Major / Emergency), meaningful ONLY once
 *      a response exists: an OWNED incident whose Incident Commander set the level
 *      (#445-ws2). NEVER rendered on a raw/un-owned/derived-aggregate surface.
 *
 *  A verbatim §5 word ("Response Level", "Emergency") on the machine axis renders a
 *  false picture ("Response Level 4 · Emergency" on a hotspot nobody is fighting) —
 *  the §4.1 DOCTRINE-MISMATCH the split exists to kill.
 *
 *  Palette (locked law — do NOT re-tune the hex values here; shared by BOTH axes,
 *  keyed on the NUMBER, so size/colour/shape are identical across the split):
 *    L1 grey  #94a3b8   ·  L2 blue  #38bdf8   ·  L3 amber #f59e0b   ·  L4 red #ef4444 + ring
 *
 *  Overlap debt this lock resolves (§3A.7): the same hues mean different things
 *  in OTHER families and must never leak across the boundary —
 *    - blue (#38bdf8 here) also means UC fire (#3b82f6) and freezing-rain CAP
 *    - amber (#f59e0b here) also means severe-storm CAP and OC-Monitored fire
 *
 *  ⛔ Do NOT import from ./fireStages or ./capHazards (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js). Color is never
 *     load-bearing alone: every render of these colours is paired with the
 *     "Threat Level N · <name>" / "Response Level N · <name>" text and the
 *     level-keyed shape (size + L4 ring).
 * ──────────────────────────────────────────────────────────────────────────
 */

export type ResponseLevel = 1 | 2 | 3 | 4;

export function isResponseLevel(v: unknown): v is ResponseLevel {
  return v === 1 || v === 2 || v === 3 || v === 4;
}

/* ───────────────────────── Urgent ↔ candidate partition (#370) ──────────────── *
 *  De-flood (founder ruling 2026-06-19): the LOCKED urgent surface — the
 *  urgent-anomalies map layer AND the standing-alarm urgency predicate
 *  (anomalyQueue.isUnackedUrgent) — is EXACTLY Response Level ≥
 *  URGENT_MIN_RESPONSE_LEVEL (the gated reds). The default-OFF fusion-candidates
 *  layer carries the demoted remainder (≤ CANDIDATE_MAX_RESPONSE_LEVEL). The two
 *  bands partition the 1–4 space exactly (URGENT_MIN === CANDIDATE_MAX + 1), so
 *  every confidence≥60 row lands on exactly one surface — never both, never
 *  dropped (pinned by hotspotBands.test.ts).
 * ───────────────────────────────────────────────────────────────────────────── */

/** Lowest Response Level rendered on the urgent surface (the gated reds). */
export const URGENT_MIN_RESPONSE_LEVEL = 3;
/** Highest Response Level carried by the fusion-candidates layer (the muted mass). */
export const CANDIDATE_MAX_RESPONSE_LEVEL = 2;

export type HotspotBand = 'urgent' | 'candidate';

/** Classify a fused hotspot's response level into its render surface. A null /
 *  out-of-range level resolves to 'candidate' (muted) — it is never urgent. */
export function classifyHotspotBand(level: number | null | undefined): HotspotBand {
  return isResponseLevel(level) && level >= URGENT_MIN_RESPONSE_LEVEL ? 'urgent' : 'candidate';
}

/**
 * The single source of the four Response Level hex values. Both the badge meta
 * and the map-dot style below derive their colour from here, so the palette
 * stays in lockstep across panel and map (§4.17 "map and panel read in
 * lockstep").
 */
export const RESPONSE_LEVEL_COLORS: Record<ResponseLevel, string> = {
  1: '#94a3b8', // slate / grey
  2: '#38bdf8', // sky / blue
  3: '#f59e0b', // amber
  4: '#ef4444', // red
};

/** Badge metadata (text label + criteria tooltip) — shared shape for both the
 *  Response Level (§5) and Threat Level (machine-severity) axes. */
export interface ResponseLevelMeta {
  /** Hex colour for the badge text + tinted background. */
  color: string;
  /** Short name for this axis (§5 tier name, or Threat severity descriptor). */
  name: string;
  /** Criteria summary, shown on hover. */
  criteria: string;
}

export const RESPONSE_LEVEL_META: Record<ResponseLevel, ResponseLevelMeta> = {
  1: {
    color: RESPONSE_LEVEL_COLORS[1],
    name: 'Small',
    criteria: 'Level 1 (Small): single organization, single site, routine response.',
  },
  2: {
    color: RESPONSE_LEVEL_COLORS[2],
    name: 'Large',
    criteria: 'Level 2 (Large): multiple organizations, EOC activated, multi-org coordination.',
  },
  3: {
    color: RESPONSE_LEVEL_COLORS[3],
    name: 'Major',
    criteria: 'Level 3 (Major): multiple sites or complex scope; provincial coordination.',
  },
  4: {
    color: RESPONSE_LEVEL_COLORS[4],
    name: 'Emergency',
    // §5 Table 1 (#514 C5): Level 4 is the ONLY level satisfying the Table's last
    // criterion (verbatim column header) — corrected from the prior "declared emergency,
    // escalation triggers met" which overstated the source (a declaration is not required;
    // "escalation triggers met" is invented).
    criteria:
      'Level 4 (Local/Provincial/National emergency): affecting whole regional area or provincial or federally declared emergency.',
  },
};

/**
 * THREAT LEVEL metadata — the MACHINE-DERIVED hazard-severity axis (#613, founder
 * §4.3 sign-off 2026-07-10). Ontario IMS has NO severity vocabulary (nothing to be
 * verbatim to), so this is an honest, doctrine-free severity descriptor that collides
 * with zero §-terms and zero reserved product names. Rendered on every raw / un-owned
 * hazard glance surface and the SITREP `response_level_max` roll-up — never on an
 * owned incident's IC-assessed §5 Response Level.
 *
 * Names are DELIBERATELY not "Severe"/"Extreme" — those collide with CAP severity
 * values (styles/capHazards). The tooltips assert NO org / EOC / declaration (a raw
 * hazard has none) — only what the number is derived from. Same 1–4 number + the
 * shared RESPONSE_LEVEL_COLORS palette, so the map disc size/colour/shape are
 * byte-identical across the split.
 */
export const THREAT_LEVEL_META: Record<ResponseLevel, ResponseLevelMeta> = {
  1: {
    color: RESPONSE_LEVEL_COLORS[1],
    name: 'Low',
    criteria:
      'Threat Level 1 (Low): machine-derived hazard severity from fire size / community proximity / CAP inputs — lowest of four.',
  },
  2: {
    color: RESPONSE_LEVEL_COLORS[2],
    name: 'Moderate',
    criteria:
      'Threat Level 2 (Moderate): machine-derived hazard severity from fire size / community proximity / CAP inputs.',
  },
  3: {
    color: RESPONSE_LEVEL_COLORS[3],
    name: 'High',
    criteria:
      'Threat Level 3 (High): machine-derived hazard severity from fire size / community proximity / CAP inputs — reaches the urgent surface.',
  },
  4: {
    color: RESPONSE_LEVEL_COLORS[4],
    name: 'Very High',
    criteria:
      'Threat Level 4 (Very High): machine-derived hazard severity from fire size / community proximity / CAP inputs — highest of four; urgent surface.',
  },
};

/* ------------------------------------------------------------------------- *
 *  Map-marker treatment for the Anomaly-Queue map surface — the AI-fused
 *  High-Risk Hotspots, Flood, and Ice-Storm anomaly layers (E.2.3 / §3B.8).
 *
 *  FOUNDER MARKER-DOCTRINE REVISION (2026-06-05, §3B.8): on the MAP, urgency is
 *  RED. Every anomaly dot is the SAME anomaly red (ANOMALY_MAP_RED) at ALL
 *  levels — the level is encoded by SIZE (12/16/22/26 px, #410 Icon Council
 *  Ruling 1 — bumped from 9/13/18/22 for legibility), the Level-4 ring, and
 *  the motion budget, NOT by hue. Only Level 3/4 pulse (the only ambient map
 *  motion besides live-camera dots); Level 1/2 are STATIC and MUTED, and are
 *  HIDDEN at country zoom — they tier in at regional+ (`hideAtCountry`). An
 *  unmapped/out-of-range level falls back to the muted Level-1-like treatment
 *  (still red, never dropped).
 *
 *  NB: this red treatment is MAP-MARKERS ONLY. The grey/blue/amber/red
 *  Response-Level palette (RESPONSE_LEVEL_COLORS / RESPONSE_LEVEL_META above) is
 *  UNCHANGED and still drives the queue rows, the side-panel badge, and the
 *  legend chips — so colour still distinguishes level everywhere the operator
 *  reads detail. §4.17 lockstep now means "map + panel agree it's the SAME
 *  anomaly", not "the dot is the badge colour".
 * ------------------------------------------------------------------------- */

/**
 * The single anomaly MAP red — urgency reads as red on the map at every level.
 * Deliberately a DISTINCT shade from flame red (#e25822, fireStages OC-FUL) and
 * the outage reds (dot #c25b52 / area #8f3530, outageStyles) so the reds read
 * apart at a glance (Colour Law 1/2 + §3B.8 / §3A.7 distinct-shades law); shape
 * (pulsing dot vs flame icon vs ambient area) and motion differ too, so colour is
 * never alone. This #ff3b30 is the reserved URGENT-RED — the ONLY pulsing red.
 */
export const ANOMALY_MAP_RED = '#ff3b30';

export interface HotspotLevelStyle {
  /** Canonical level (1–4) or 'unknown'. */
  level: ResponseLevel | 'unknown';
  /** CSS hex for the dot + (Level 4) outer ring — the anomaly map red. */
  color: string;
  /** Resting dot diameter in px (SIZE encodes Response Level). */
  baseSize: number;
  /** Px added at the pulse peak. Only meaningful when `pulse` is true (L3/L4). */
  pulseAmplitude: number;
  /** Dot fill opacity. L1/L2 are MUTED (lower alpha); L3/L4 are vivid. */
  alpha: number;
  /** Level 4 only: render an animated low-alpha outer ring behind the dot. */
  ring: boolean;
  /** Motion budget (§3B.8): only L3/L4 pulse; L1/L2 + unknown are STATIC. */
  pulse: boolean;
  /** L1/L2 (+ unknown) are HIDDEN at country zoom and tier in at regional+;
   *  L3/L4 are visible at all zoom tiers (always-on urgent signal). */
  hideAtCountry: boolean;
  /** Short label for a legend / tooltip — color is never alone. */
  label: string;
}

/* ───────────────────────── ICON SIZE LADDER — the ONE tunable block ───────────────────────── *
 *  Founder NIGHT ruling 1 (2026-07-06, docs/founder-directives-2026-07-06.md §5): round 1 made the
 *  discs TOO SMALL. Recalibrate — RL4 markers are BIGGER and DOMINANT at national zoom; lower
 *  levels render "reasonably smaller." The RL-ordering law and the cross-family RL4 floor STAND
 *  (pinned by crossFamilyIconSize.lock.test.ts); only the absolute values + the country-zoom shrink
 *  are retuned, and the FOUNDER EYEBALL gates the final numbers.
 *
 *  TWO knobs per level, BOTH here so the whole ladder tunes in one place (no scattered sizes):
 *    • baseSize     — resting (regional-zoom) disc diameter, px. SIZE is the redundant level cue
 *                     (icon-council R1). Every disc family routes its size through here.
 *    • countryFloor — the national/country-zoom shrink FLOOR on the shared #410 declutter curve
 *                     (NearFarScalar farValue, applied by discCountryScale/anomalyScale): RL4 barely
 *                     shrinks (≥0.85× → stays dominant nationally, founder ruling), L3 shrinks more
 *                     (~0.7×), L1/L2 are country-HIDDEN so their floor only bites an always-visible
 *                     OC fire at that level (kept lowest so a low fire never rivals a red RL4).
 *
 *  These are CTO starting values (26→34 for RL4, 22→24 for L3); the founder finalizes on eyeball. */
export const ICON_LADDER: Record<ResponseLevel, { baseSize: number; countryFloor: number }> = {
  1: { baseSize: 12, countryFloor: 0.55 },
  2: { baseSize: 16, countryFloor: 0.55 },
  3: { baseSize: 24, countryFloor: 0.7 }, // #578 was 22
  4: { baseSize: 34, countryFloor: 0.85 }, // #578 was 26 — NIGHT ruling 1: bigger + dominant
};

/** National-zoom shrink floor for an unknown / unscored level — treated like a low level so an
 *  always-visible OC fire with a null response level never bloats past a red RL4 marker. */
export const ICON_LADDER_UNKNOWN_FLOOR = 0.55;

const LEVEL_DOTS: Record<ResponseLevel, HotspotLevelStyle> = {
  1: {
    level: 1,
    color: ANOMALY_MAP_RED,
    baseSize: ICON_LADDER[1].baseSize, // size ladder (tunable block above)
    pulseAmplitude: 0,
    alpha: 0.5, // muted
    ring: false,
    pulse: false, // static
    hideAtCountry: true, // tiers in at regional+
    label: `Level 1 · ${THREAT_LEVEL_META[1].name}`, // map dots = raw hazards → Threat axis (#613)
  },
  2: {
    level: 2,
    color: ANOMALY_MAP_RED,
    baseSize: ICON_LADDER[2].baseSize, // size ladder (tunable block above)
    pulseAmplitude: 0,
    alpha: 0.62, // muted
    ring: false,
    pulse: false, // static
    hideAtCountry: true, // tiers in at regional+
    label: `Level 2 · ${THREAT_LEVEL_META[2].name}`, // map dots = raw hazards → Threat axis (#613)
  },
  3: {
    level: 3,
    color: ANOMALY_MAP_RED,
    baseSize: ICON_LADDER[3].baseSize, // size ladder (tunable block above)
    pulseAmplitude: 6, // scaled with the bump (5→6)
    alpha: 0.92,
    ring: false,
    pulse: true,
    hideAtCountry: false, // always-on urgent signal
    label: `Level 3 · ${THREAT_LEVEL_META[3].name}`, // map dots = raw hazards → Threat axis (#613)
  },
  4: {
    level: 4,
    color: ANOMALY_MAP_RED,
    baseSize: ICON_LADDER[4].baseSize, // size ladder (tunable block above) — dominant at national zoom
    pulseAmplitude: 7, // scaled with the bump (6→7)
    alpha: 0.97,
    ring: true,
    pulse: true,
    hideAtCountry: false, // always-on urgent signal
    label: `Level 4 · ${THREAT_LEVEL_META[4].name}`, // map dots = raw hazards → Threat axis (#613)
  },
};

const UNKNOWN_DOT: HotspotLevelStyle = {
  level: 'unknown',
  color: ANOMALY_MAP_RED,
  baseSize: ICON_LADDER[1].baseSize, // matches L1 (treated like a low level)
  pulseAmplitude: 0,
  alpha: 0.5, // muted, treated like a low level
  ring: false,
  pulse: false, // static
  hideAtCountry: true,
  label: 'Unknown level',
};

/**
 * Resolve the render style for a response_level value. Tolerant of null /
 * out-of-range (resolves to the grey Level-1-ish 'unknown' style); callers that
 * need to flag the unmapped case should check `isResponseLevel` themselves.
 */
export function hotspotLevelStyle(level: number | null | undefined): HotspotLevelStyle {
  return isResponseLevel(level) ? LEVEL_DOTS[level] : UNKNOWN_DOT;
}
